import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, Switch, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Stack, CircularProgress, Snackbar, Alert, IconButton, Tooltip,
} from '@mui/material';
import { Refresh, Add, Edit, Delete } from '@mui/icons-material';
import PaymentGatewayService from '../../services/paymentGateway.service';

const EMPTY_CREATE = { gateway_key: '', display_name: '', fee_percentage: 0, fee_fixed: 0, enabled: true };

const PaymentGateways = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createForm, setCreateForm] = useState(null);
  const [edit, setEdit] = useState(null); // { id, display_name, fee_percentage, fee_fixed }
  const [confirmDelete, setConfirmDelete] = useState(null); // gateway row
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(() => {
    setLoading(true);
    PaymentGatewayService.list()
      .then((list) => setRows(Array.isArray(list) ? list : []))
      .catch(() => notify('Failed to load gateways', 'error'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (g) => {
    PaymentGatewayService.toggle(g.id)
      .then(() => { notify(g.enabled ? 'Gateway disabled' : 'Gateway enabled'); load(); })
      .catch(() => notify('Update failed', 'error'));
  };

  const create = () => {
    if (!createForm.gateway_key || !createForm.gateway_key.trim()) {
      notify('Gateway key is required', 'error');
      return;
    }
    setSaving(true);
    PaymentGatewayService.create({
      gateway_key: createForm.gateway_key.trim().toUpperCase(),
      display_name: createForm.display_name,
      fee_percentage: Number(createForm.fee_percentage) || 0,
      fee_fixed: Number(createForm.fee_fixed) || 0,
      enabled: createForm.enabled,
    })
      .then(() => { notify('Gateway added'); setCreateForm(null); load(); })
      .catch((e) => notify(e?.response?.data?.message || 'Create failed', 'error'))
      .finally(() => setSaving(false));
  };

  const saveEdit = () => {
    setSaving(true);
    PaymentGatewayService.update(edit.id, {
      display_name: edit.display_name,
      fee_percentage: Number(edit.fee_percentage) || 0,
      fee_fixed: Number(edit.fee_fixed) || 0,
    })
      .then(() => { notify('Gateway updated'); setEdit(null); load(); })
      .catch((e) => notify(e?.response?.data?.message || 'Update failed', 'error'))
      .finally(() => setSaving(false));
  };

  const doDelete = () => {
    setSaving(true);
    PaymentGatewayService.remove(confirmDelete.id)
      .then(() => { notify('Gateway deleted'); setConfirmDelete(null); load(); })
      .catch((e) => notify(e?.response?.data?.message || 'Delete failed', 'error'))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Payment Gateways</Typography>
          <Typography variant="caption" color="text.secondary">Enable a gateway and set the platform fee charged to typing centers on online payments.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateForm({ ...EMPTY_CREATE })}>Add gateway</Button>
        </Stack>
      </Stack>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Gateway</TableCell><TableCell>Key</TableCell><TableCell>Fee %</TableCell>
                  <TableCell>Fixed fee</TableCell><TableCell>Enabled</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No gateways configured</Typography></TableCell></TableRow>
                ) : rows.map((g) => (
                  <TableRow key={g.id} hover>
                    <TableCell>{g.display_name || g.gateway_key}</TableCell>
                    <TableCell>{g.gateway_key}</TableCell>
                    <TableCell>{g.fee_percentage}%</TableCell>
                    <TableCell>{g.fee_fixed}</TableCell>
                    <TableCell><Switch checked={!!g.enabled} onChange={() => toggle(g)} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => setEdit({ id: g.id, display_name: g.display_name || '', fee_percentage: g.fee_percentage, fee_fixed: g.fee_fixed })}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setConfirmDelete(g)}><Delete fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add gateway dialog */}
      <Dialog open={!!createForm} onClose={() => !saving && setCreateForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add payment gateway</DialogTitle>
        <DialogContent>
          {createForm && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Gateway key" value={createForm.gateway_key}
                onChange={(e) => setCreateForm({ ...createForm, gateway_key: e.target.value })}
                helperText="Matches a supported gateway, e.g. NGENIUS" />
              <TextField label="Display name" value={createForm.display_name}
                onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })} />
              <TextField label="Fee percentage" type="number" value={createForm.fee_percentage}
                onChange={(e) => setCreateForm({ ...createForm, fee_percentage: e.target.value })} />
              <TextField label="Fixed fee" type="number" value={createForm.fee_fixed}
                onChange={(e) => setCreateForm({ ...createForm, fee_fixed: e.target.value })} />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2">Enabled</Typography>
                <Switch checked={createForm.enabled} onChange={(e) => setCreateForm({ ...createForm, enabled: e.target.checked })} />
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateForm(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={create} disabled={saving}>{saving ? 'Saving…' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!edit} onClose={() => !saving && setEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit gateway</DialogTitle>
        <DialogContent>
          {edit && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Display name" value={edit.display_name}
                onChange={(e) => setEdit({ ...edit, display_name: e.target.value })} />
              <TextField label="Fee percentage" type="number" value={edit.fee_percentage}
                onChange={(e) => setEdit({ ...edit, fee_percentage: e.target.value })} />
              <TextField label="Fixed fee" type="number" value={edit.fee_fixed}
                onChange={(e) => setEdit({ ...edit, fee_fixed: e.target.value })} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onClose={() => !saving && setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete gateway</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <b>{confirmDelete?.display_name || confirmDelete?.gateway_key}</b>? Online payments via this gateway will be unavailable.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={doDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentGateways;
